/**
 * «Da decidere» — brief part 1 §6 band 3 (left), audit v6 V6-02.
 *
 * A one-line table: Coda · App · N. · Valore · →, 40px rows, ONE row per queue, the whole row a way
 * into the queue's page on the run it counted. There is no «Apri» button and no second control per
 * row: the "Explain" entry the list used to carry is on the queue's own page (its explain view),
 * where the recommendation it explains is drawn.
 *
 * Every N is a real number from a read the Cockpit makes (`decisionRows`); a read that has not
 * answered contributes no row rather than a zero. Where a queue's N is its whole book rather than
 * what is pending (a platform that does not record the decisions), the queue's name says so —
 * «Price steps in the run», not «to approve» — and the row's tooltip says why.
 *
 * **Valore** is the pending lines' value as the platform publishes it (bata-platform ADR 6036), in
 * its own currency, never summed with another row (ADR 9017). Where it is not published, withheld
 * (several currencies) or the queue's decisions carry no money, the cell says so in words
 * («Nessun importo», ADR 9061), and the reasons are said ONCE, under the table (DEC-96: never estimated).
 */

import { useTranslation } from 'react-i18next';

import { formatCompactNumber, formatCurrency } from '@/lib/format';

import { periodWords } from '@/planning';

import { RAIL_APPS, type RailAppId } from '../../chrome/rail-layout';
import type { DecisionId, DecisionRow, DecisionStake } from './cockpit';
import { DECISION_TARGET } from './decision-link';

/**
 * The app a path belongs to, as the rail groups it (`RAIL_APPS`, ADR 9023) — the longest entry
 * that is the path or a parent of it. Read from the rail rather than listed here, so the App
 * column cannot disagree with the menu the planner is about to use.
 */
export function appOfPath(path: string): RailAppId | null {
  let best: { readonly app: RailAppId; readonly length: number } | null = null;
  for (const app of RAIL_APPS) {
    for (const entry of app.entries) {
      const matches = path === entry.to || path.startsWith(`${entry.to}/`);
      if (matches && (best === null || entry.to.length > best.length)) best = { app: app.id, length: entry.to.length };
    }
  }
  return best?.app ?? null;
}

const APP_LABEL: Readonly<Record<RailAppId, string>> = {
  pricing: 'nav.app.pricing',
  promotions: 'nav.app.promotions',
  replenishment: 'nav.app.replenishment',
  compacting: 'nav.app.compacting',
  health: 'nav.app.health',
  management: 'nav.app.management',
  initialAllocation: 'nav.app.initialAllocation',
  assortment: 'nav.app.assortment',
};

/** Each queue's name, and the sentence its tooltip says — spelled out for the catalogue check. */
const QUEUE_KEYS: Readonly<Record<DecisionId, { readonly name: string; readonly detail: string }>> = {
  refusedCandidates: { name: 'overview.decide.queue.refusedCandidates', detail: 'overview.decide.refusedCandidates.detail' },
  priceSteps: { name: 'overview.decide.queue.priceSteps', detail: 'overview.decide.priceSteps.detail' },
  replenishment: { name: 'overview.decide.queue.replenishment', detail: 'overview.decide.replenishment.detail' },
  transfers: { name: 'overview.decide.queue.transfers', detail: 'overview.decide.transfers.detail' },
  openAlerts: { name: 'overview.decide.queue.openAlerts', detail: 'overview.decide.openAlerts.detail' },
  writeBack: { name: 'overview.decide.queue.writeBack', detail: 'overview.decide.writeBack.detail' },
  ladderExpiry: { name: 'overview.decide.queue.ladderExpiry', detail: 'overview.decide.ladderExpiry.detail' },
  pricingCoverage: { name: 'overview.decide.queue.pricingCoverage', detail: 'overview.decide.pricingCoverage.detail' },
};

/** Why a value is «—», in the planner's words — each said once, under the table. */
type DashReason = 'mixedCurrencies' | 'notPublished' | 'unrecognised' | 'noMoney';

const DASH_REASON_KEY: Readonly<Record<DashReason, string>> = {
  mixedCurrencies: 'overview.decide.stake.mixedCurrencies',
  notPublished: 'overview.decide.stake.notPublished',
  unrecognised: 'overview.decide.stake.unrecognised',
  noMoney: 'overview.decide.stake.noMoney',
};

/** The same four, in two words for the cell itself (ADR 9061) — the sentence above stays under the table. */
const STAKE_LABEL_KEY: Readonly<Record<DashReason, string>> = {
  mixedCurrencies: 'overview.decide.stake.label.mixedCurrencies',
  notPublished: 'overview.decide.stake.label.notPublished',
  unrecognised: 'overview.decide.stake.label.unrecognised',
  noMoney: 'overview.decide.stake.label.noMoney',
};

const DASH_ORDER: readonly DashReason[] = ['notPublished', 'mixedCurrencies', 'unrecognised', 'noMoney'];

/** Why a row's value is «—», or `null` when it has one. */
export function dashReason(stake: DecisionStake | undefined): DashReason | null {
  if (stake === undefined) return 'noMoney';
  return stake.kind === 'money' ? null : stake.reason;
}

export interface NeedsDecisionProps {
  readonly rows: readonly DecisionRow[];
  /** True while any read a row could come from has not answered. */
  readonly settling: boolean;
  readonly onOpen: (id: DecisionId) => void;
}

const COLUMN_HEAD = 'text-ink-primary px-[10px] py-2 text-[11px] font-bold uppercase tracking-[0.06em]';

export function NeedsDecision({ rows, settling, onOpen }: NeedsDecisionProps): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const reasons = DASH_ORDER.filter((reason) => rows.some((row) => dashReason(row.stake) === reason));

  return (
    <section aria-labelledby="cockpit-decide" className="flex min-w-0 grow flex-col" data-needs-decision="">
      <h2 id="cockpit-decide" className="text-ink-primary font-display pb-2 text-section font-bold uppercase">
        {t('overview.decide.title')}
      </h2>
      {rows.length === 0 ? (
        <p className="text-body text-ink-secondary border-ink-primary border-t-2 py-3">
          {t(settling ? 'state.loading' : 'overview.decide.none')}
        </p>
      ) : (
        <>
          <table className="w-full table-fixed border-collapse text-[13px]">
            {/* A number is never cut (usability audit D4: «67,9 Mln COP» lost its end, and a cut
                number reads as another number). The figure columns are sized for the longest
                compact figure the three locales write — «999 mil M COP» in es-419 — and the
                currency, not the figure, is what moves to a second line if a font runs wider. */}
            <colgroup>
              <col />
              <col className="w-[136px]" />
              <col className="w-[84px]" />
              <col className="w-[132px]" />
              <col className="w-[24px]" />
            </colgroup>
            <thead>
              <tr className="border-ink-primary border-b border-t-2">
                <th scope="col" className={`${COLUMN_HEAD} text-start`}>{t('overview.decide.column.queue')}</th>
                <th scope="col" className={`${COLUMN_HEAD} text-start`}>{t('overview.decide.column.app')}</th>
                <th scope="col" className={`${COLUMN_HEAD} text-end`}>{t('overview.decide.column.count')}</th>
                <th scope="col" className={`${COLUMN_HEAD} text-end`}>{t('overview.decide.column.value')}</th>
                <td aria-hidden />
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const count = formatCompactNumber(row.count, i18n.language);
                const app = appOfPath(DECISION_TARGET[row.id]);
                // A week reads as words («settimana 41 del 2026»), never as the platform's `2026-W41`.
                const week = row.values.week;
                // `cockpit.ts` marks a value the platform did not state with the dash sentinel (data, not
                // copy); it is said in words here, never drawn (ADR 9061).
                const stated = Object.fromEntries(Object.entries(row.values).map(([key, value]) => [key, value === '—' ? t('absent.word.notStated') : value]));
                const values = { count: row.count, ...stated, ...(typeof week === 'string' && week !== '—' ? { week: periodWords(week, t, i18n.language) } : {}) };
                const detail = t(QUEUE_KEYS[row.id].detail, values);
                const stake = row.stake;
                return (
                  <tr
                    key={row.id}
                    data-decision={row.id}
                    // The row is the link (brief §6): the queue's button is stretched over the
                    // whole row by its `after:` box, so a click anywhere opens the queue and the
                    // table keeps its column semantics for a screen reader.
                    className="border-hairline hover:bg-surface-emphasis relative h-[40px] border-b"
                  >
                    <td className="min-w-0 px-[10px]">
                      {row.unlisted === true ? (
                        // A number no list totals opens no list (ADR 9069): the name is text, and the note below says why.
                        <span title={detail} data-decision-unlisted="" className="text-ink-primary block w-full truncate text-start font-semibold">
                          {t(QUEUE_KEYS[row.id].name, values)}
                        </span>
                      ) : (
                        <button
                          type="button"
                          onClick={() => onOpen(row.id)}
                          title={detail}
                          className="text-ink-primary block w-full truncate text-start font-semibold after:absolute after:inset-0 focus-visible:outline-none focus-visible:after:outline focus-visible:after:outline-2 focus-visible:after:-outline-offset-2 focus-visible:after:outline-focus-ring"
                        >
                          {t(QUEUE_KEYS[row.id].name, values)}
                        </button>
                      )}
                    </td>
                    <td className="text-ink-label truncate px-[10px]" {...(app === null ? {} : { title: t(APP_LABEL[app]) })}>
                      {app === null ? t('absent.notStated') : t(APP_LABEL[app])}
                    </td>
                    <td
                      className={`font-numeric whitespace-nowrap px-[10px] text-end font-semibold tabular-nums ${row.id === 'refusedCandidates' ? 'text-status-critical-strong' : 'text-ink-primary'}`}
                      title={count.exact}
                    >
                      {count.text}
                    </td>
                    <td className="font-numeric px-[10px] text-end tabular-nums" data-stake={stake?.kind ?? 'none'}>
                      {stake?.kind === 'money' ? (
                        <span className="text-ink-primary" title={formatCurrency(stake.amount, stake.currency, i18n.language)}>
                          <span className="whitespace-nowrap">{formatCompactNumber(stake.amount, i18n.language).text}</span>
                          {' '}
                          <span className="whitespace-nowrap">{stake.currency}</span>
                        </span>
                      ) : <span className="text-ink-secondary whitespace-nowrap">{t(STAKE_LABEL_KEY[dashReason(stake) ?? 'noMoney'])}</span>}
                    </td>
                    <td aria-hidden className="text-ink-label">{row.unlisted === true ? '' : '→'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {rows.some((row) => row.unlisted === true) && (
            <p className="text-caption text-ink-secondary pt-2" data-unlisted-note="">{t('overview.decide.unlisted')}</p>
          )}
          {/* Each absence explained once for the table, «<words>: <reason>» (brief §4: the reason is said once). */}
          {reasons.length > 0 && (
            <p className="text-caption text-ink-secondary flex flex-wrap gap-x-3 pt-2" data-value-note="">
              {reasons.map((reason) => (
                <span key={reason}>
                  <span className="font-semibold">{t('table.absence.noteLabel', { label: t(STAKE_LABEL_KEY[reason]) })}</span>
                  {' '}
                  {t(DASH_REASON_KEY[reason])}
                </span>
              ))}
            </p>
          )}
        </>
      )}
    </section>
  );
}
